import { formatUnits } from 'viem';
import {
  getNetworkNativeTokenSymbol,
  nativeTokenDecimals,
  type NetworkType,
} from '@mls-wallet/shared-types';
import { formatAddress } from '@/lib/utils';
import type { BatchTransactionChild } from './batch-transaction-draft';
import { ReviewTokenTransferDetails } from './ReviewTokenTransferDetails';

/** Describe the saved payload using only metadata from its own editor. */
export function BatchTransactionSummary({
  child,
  network,
  review = false,
}: {
  child: BatchTransactionChild;
  network: NetworkType;
  review?: boolean;
}) {
  const editor = child.editor;
  // Case: a transfer saved from the editor retains its token and recipient metadata.
  if (editor?.transactionType === 'token-transfer') {
    const details = editor.tokenTransferDetails;
    const label =
      details.recipient.label || formatAddress(details.recipient.address);
    // Case: review shows the transfer amount, asset and recipient as badges.
    if (review)
      return (
        <div className="[&>div]:justify-start">
          <ReviewTokenTransferDetails
            displayAmount={details.displayAmount}
            tokenTransferDetails={details}
            recipientLabel={label}
          />
        </div>
      );
    return (
      <span>
        Send{' '}
        <strong className="font-semibold">
          {details.displayAmount} {details.asset.symbol}
        </strong>{' '}
        to{' '}
        <strong className="font-semibold">
          {details.recipient.label ? `${details.recipient.label} · ` : ''}
          {formatAddress(details.recipient.address)}
        </strong>
      </span>
    );
  }
  // Case: empty calldata identifies a native send, summarized from the saved payload.
  if (child.payload.data === '0x') {
    return (
      <span>
        Send{' '}
        <strong className="font-semibold">
          {formatUnits(BigInt(child.payload.value), nativeTokenDecimals)}{' '}
          {getNetworkNativeTokenSymbol(network)}
        </strong>{' '}
        to{' '}
        <strong className="font-semibold">
          {formatAddress(child.payload.to)}
        </strong>
      </span>
    );
  }
  const details =
    editor?.transactionType === 'contract-interaction'
      ? editor.contractInteractionDetails
      : undefined;
  const name =
    details?.selectedFunction?.name ??
    child.payload.functionName ??
    child.payload.data.slice(0, 10);
  const contract = details?.contractName || formatAddress(child.payload.to);
  const parameters = details?.functionParameters?.map(({ parsedValue }) => {
    if (typeof parsedValue === 'string')
      return /^0x[\da-f]{40}$/i.test(parsedValue)
        ? formatAddress(parsedValue)
        : parsedValue;
    if (typeof parsedValue === 'number' || typeof parsedValue === 'boolean')
      return String(parsedValue);
    return Array.isArray(parsedValue) ? `${parsedValue.length} items` : 'tuple';
  });
  return (
    <span className="break-words">
      Call{' '}
      <span
        className={
          review
            ? 'rounded-lg bg-muted px-1.5 font-mono text-[15px]'
            : 'font-mono text-[15px]'
        }
      >
        {name}
      </span>{' '}
      on{' '}
      <span
        className={
          review
            ? 'rounded-lg bg-muted px-1.5 font-mono text-[15px]'
            : 'font-mono text-[15px]'
        }
      >
        {contract}
      </span>
      {!review && !!parameters?.length && (
        <>
          {' '}
          with{' '}
          <span className="font-mono text-[15px]">
            {parameters.join(' · ')}
          </span>
        </>
      )}
    </span>
  );
}
